import { PatientInfo } from './types';
import * as DataGenerator from './generate-data';

// Re-export interfaces from types.ts for better organization
export interface Location {
  id: string;
  name: string;
  type: 'hospital' | 'diagnostic';
  address: string;
  area: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  services: {
    examinations: boolean;
    specialists: boolean;
  };
}

export interface Examination {
  id: string;
  name: {
    el: string; // Name in Greek
    en: string; // Name in English
  };
  code: string;
  duration: number; // Duration in minutes
  preparationNeeded: boolean;
  preparationInstructions?: {
    el: string;
    en: string;
  };
}

export interface Specialty {
  id: string;
  name: {
    el: string;
    en: string;
  };
}

export interface Doctor {
  id: string;
  firstName: {
    el: string;
    en: string;
  };
  lastName: {
    el: string;
    en: string;
  };
  specialtyId: string;
  locationIds: string[]; // IDs of locations where the doctor works
  languages: string[]; // ISO codes
  appointments: {
    duration: number; // Appointment duration in minutes
    daysAvailable: number[]; // 0=Sunday, 1=Monday, etc.
  };
}

export interface TimeSlot {
  id: string;
  date: string; // ISO format 'YYYY-MM-DD'
  startTime: string; // format 'HH:MM'
  locationId: string;
  serviceType: 'examination' | 'doctor';
  serviceId: string; // ID of the doctor or examination
  booked: boolean;
}

export interface AlternativesResult {
  type:
    | 'different-location'
    | 'different-doctor'
    | 'different-time'
    | 'different-day'
    | 'no-alternatives';
  slots: TimeSlot[];
  details?: {
    // eslint-disable-next-line
    [key: string]: any;
  };
}

// Main service class for health care
export class HealthCareService {
  private locations: Location[];
  private examinations: Examination[];
  private specialties: Specialty[];
  private doctors: Doctor[];
  private slots: TimeSlot[];

  constructor() {
    // Initialize with generated data
    // Here we're using the data directly from generate-data.ts
    const data = DataGenerator.generateHealthCareData();
    this.locations = data.locations;
    this.examinations = data.examinations;
    this.specialties = data.specialties;
    this.doctors = data.doctors;
    this.slots = data.slots;
  }

  // Method to get all locations
  getLocations(): Location[] {
    return this.locations;
  }

  // Method to get all examinations
  getExaminations(): Examination[] {
    return this.examinations;
  }

  // Method to get all specialties
  getSpecialties(): Specialty[] {
    return this.specialties;
  }

  // Method to get all doctors
  getDoctors(): Doctor[] {
    return this.doctors;
  }

  // Method to get doctors by specialty
  getDoctorsBySpecialty(specialtyId: string): Doctor[] {
    return this.doctors.filter(doctor => doctor.specialtyId === specialtyId);
  }

  // Method to get doctors available at a location
  getDoctorsByLocation(locationId: string): Doctor[] {
    return this.doctors.filter(doctor =>
      doctor.locationIds.includes(locationId)
    );
  }

  // Method to get a doctor by ID
  getDoctorById(doctorId: string): Doctor | undefined {
    return this.doctors.find(doctor => doctor.id === doctorId);
  }

  // Method to get a specialty by ID
  getSpecialtyById(specialtyId: string): Specialty | undefined {
    return this.specialties.find(specialty => specialty.id === specialtyId);
  }

  // Method to get an examination by ID
  getExaminationById(examinationId: string): Examination | undefined {
    return this.examinations.find(
      examination => examination.id === examinationId
    );
  }

  // Method to get a location by ID
  getLocationById(locationId: string): Location | undefined {
    return this.locations.find(location => location.id === locationId);
  }

  // Method to find available slots
  findAvailableSlots(params: {
    date?: string;
    locationId?: string;
    serviceType: 'examination' | 'doctor';
    serviceId: string;
  }): TimeSlot[] {
    // Si estamos buscando slots para un doctor, necesitamos respetar la regla
    // de que un doctor solo está disponible en una ubicación por día
    if (params.serviceType === 'doctor' && params.date) {
      // Usar nuestra nueva función para determinar la ubicación del doctor en esa fecha
      const doctorLocation = this.getDoctorLocationForDate(
        params.serviceId,
        params.date
      );

      // Si se especificó una ubicación en los parámetros y no coincide con donde
      // estará el doctor ese día, devolver un array vacío
      if (
        params.locationId &&
        doctorLocation &&
        doctorLocation.locationId !== params.locationId
      ) {
        return [];
      }

      // Si no se especificó una ubicación, añadirla a los parámetros para filtrar correctamente
      if (!params.locationId && doctorLocation) {
        params = {
          ...params,
          locationId: doctorLocation.locationId
        };
      }
    }

    // El resto de la función original
    return this.slots.filter(
      slot =>
        slot.serviceType === params.serviceType &&
        slot.serviceId === params.serviceId &&
        (!params.date || slot.date === params.date) &&
        (!params.locationId || slot.locationId === params.locationId)
    );
  }

  // Method to find alternatives when preferred slot is not available
  findAlternatives(params: {
    preferredDate: string;
    preferredTime: string;
    preferredLocationId: string;
    serviceType: 'examination' | 'doctor';
    serviceId: string;
  }): AlternativesResult {
    // 1. Try same time at other locations
    const sameTimeOtherLocations = this.slots.filter(
      slot =>
        !slot.booked &&
        slot.date === params.preferredDate &&
        slot.startTime === params.preferredTime &&
        slot.locationId !== params.preferredLocationId &&
        slot.serviceType === params.serviceType &&
        slot.serviceId === params.serviceId
    );

    if (sameTimeOtherLocations.length > 0) {
      // Add location information
      const enhancedSlots = sameTimeOtherLocations.map(slot => {
        const location = this.getLocationById(slot.locationId);
        return {
          ...slot,
          locationName: location?.name || '',
          locationArea: location?.area || ''
        };
      });

      return {
        type: 'different-location',
        slots: enhancedSlots,
        details: {
          message: 'Same time available at other locations'
        }
      };
    }

    // 2. If it's a doctor appointment, try another doctor of the same specialty
    if (params.serviceType === 'doctor') {
      const doctor = this.getDoctorById(params.serviceId);
      if (doctor) {
        const sameSpecialtyDoctors = this.doctors.filter(
          d =>
            d.id !== params.serviceId &&
            d.specialtyId === doctor.specialtyId &&
            d.locationIds.includes(params.preferredLocationId)
        );

        const alternativeDoctorSlots = this.slots.filter(
          slot =>
            !slot.booked &&
            slot.date === params.preferredDate &&
            slot.startTime === params.preferredTime &&
            slot.locationId === params.preferredLocationId &&
            slot.serviceType === 'doctor' &&
            sameSpecialtyDoctors.some(d => d.id === slot.serviceId)
        );

        if (alternativeDoctorSlots.length > 0) {
          // Add doctor information
          const enhancedSlots = alternativeDoctorSlots.map(slot => {
            const doc = this.getDoctorById(slot.serviceId);
            const specialty = doc
              ? this.getSpecialtyById(doc.specialtyId)
              : undefined;
            return {
              ...slot,
              doctorName: doc ? `${doc.firstName.el} ${doc.lastName.el}` : '',
              specialtyName: specialty?.name.el || ''
            };
          });

          return {
            type: 'different-doctor',
            slots: enhancedSlots,
            details: {
              message: 'Same time available with another specialist'
            }
          };
        }
      }
    }

    // 3. Try nearby times at same location and day
    const sameLocationSameDay = this.slots
      .filter(
        slot =>
          !slot.booked &&
          slot.date === params.preferredDate &&
          slot.locationId === params.preferredLocationId &&
          slot.serviceType === params.serviceType &&
          slot.serviceId === params.serviceId
      )
      .sort((a, b) => {
        // Sort by time difference from preferred time
        const timeA = this.timeToMinutes(a.startTime);
        const timeB = this.timeToMinutes(b.startTime);
        const preferredTime = this.timeToMinutes(params.preferredTime);

        return (
          Math.abs(timeA - preferredTime) - Math.abs(timeB - preferredTime)
        );
      });

    if (sameLocationSameDay.length > 0) {
      return {
        type: 'different-time',
        slots: sameLocationSameDay,
        details: {
          message: 'Different times available on the same day and location'
        }
      };
    }

    // 4. Look for nearby days
    const nearbyDays = this.slots
      .filter(
        slot =>
          !slot.booked &&
          slot.date > params.preferredDate &&
          slot.date <= this.getDatePlusDays(params.preferredDate, 7) &&
          slot.locationId === params.preferredLocationId &&
          slot.serviceType === params.serviceType &&
          slot.serviceId === params.serviceId
      )
      .sort(
        (a, b) =>
          a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime)
      );

    if (nearbyDays.length > 0) {
      // Group by date
      const groupedByDate: { [key: string]: TimeSlot[] } = {};
      nearbyDays.forEach(slot => {
        if (!groupedByDate[slot.date]) {
          groupedByDate[slot.date] = [];
        }
        groupedByDate[slot.date].push(slot);
      });

      return {
        type: 'different-day',
        slots: nearbyDays,
        details: {
          message: 'Appointments available in the coming days',
          groupedByDate
        }
      };
    }

    return {
      type: 'no-alternatives',
      slots: [],
      details: {
        message: 'No available alternatives found'
      }
    };
  }

  // Method to book an appointment
  // eslint-disable-next-line
  bookAppointment(slotId: string, patientInfo: PatientInfo): { success: boolean; message: string; appointment?: TimeSlot } {
    const slotIndex = this.slots.findIndex(s => s.id === slotId);

    if (slotIndex === -1) {
      return {
        success: false,
        message: 'Slot not found'
      };
    }

    if (this.slots[slotIndex].booked) {
      return {
        success: false,
        message: 'This slot is already booked'
      };
    }

    // Book the slot
    this.slots[slotIndex] = {
      ...this.slots[slotIndex],
      booked: true
    };

    return {
      success: true,
      message: 'Appointment booked successfully',
      appointment: this.slots[slotIndex]
    };
  }

  // Method to cancel an appointment
  cancelAppointment(slotId: string): { success: boolean; message: string } {
    const slotIndex = this.slots.findIndex(s => s.id === slotId);

    if (slotIndex === -1) {
      return {
        success: false,
        message: 'Slot not found'
      };
    }

    if (!this.slots[slotIndex].booked) {
      return {
        success: false,
        message: 'This slot is not booked'
      };
    }

    // Cancel the booking
    this.slots[slotIndex] = {
      ...this.slots[slotIndex],
      booked: false
    };

    return {
      success: true,
      message: 'Appointment cancelled successfully'
    };
  }

  // Method to get detailed appointment information
  getAppointmentDetails(slotId: string): {
    slot: TimeSlot | null;
    location?: Location;
    service?: Examination | Doctor;
    serviceType?: 'examination' | 'doctor';
    specialty?: Specialty;
  } {
    const slot = this.slots.find(s => s.id === slotId);

    if (!slot) {
      return { slot: null };
    }

    const location = this.getLocationById(slot.locationId);

    if (slot.serviceType === 'examination') {
      const examination = this.getExaminationById(slot.serviceId);
      return {
        slot,
        location,
        service: examination,
        serviceType: 'examination'
      };
    } else {
      const doctor = this.getDoctorById(slot.serviceId);
      const specialty = doctor
        ? this.getSpecialtyById(doctor.specialtyId)
        : undefined;
      return {
        slot,
        location,
        service: doctor,
        serviceType: 'doctor',
        specialty
      };
    }
  }

  // Helper methods
  private timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private getDatePlusDays(dateStr: string, days: number): string {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  }

  /**
   * Determina en qué ubicación estará un doctor en una fecha específica
   * @param doctorId ID del doctor
   * @param dateStr Fecha en formato ISO YYYY-MM-DD
   * @returns ID de la ubicación, nombre de la ubicación o null si el doctor no está disponible
   */
  getDoctorLocationForDate(
    doctorId: string,
    dateStr: string
  ): { locationId: string; locationName: string } | null {
    // Encontrar el doctor
    const doctor = this.getDoctorById(doctorId);
    if (!doctor) return null;

    // Verificar si el doctor trabaja ese día de la semana
    const date = new Date(dateStr);
    const dayOfWeek = date.getDay();

    if (!doctor.appointments.daysAvailable.includes(dayOfWeek)) {
      return null; // El doctor no trabaja este día
    }

    // Usar el mismo algoritmo determinista que en generateTimeSlots
    const dateHash = dateStr
      .split('-')
      .reduce((acc, curr) => acc + parseInt(curr, 10), 0);
    const doctorIdHash =
      parseInt(doctor.id.replace(/\D/g, '')) || doctor.id.charCodeAt(0);
    const combinedHash = (dateHash + doctorIdHash) % doctor.locationIds.length;

    const locationId = doctor.locationIds[combinedHash];
    const location = this.getLocationById(locationId);

    return {
      locationId,
      locationName: location ? location.name : 'Unknown Location'
    };
  }
}

// Export a singleton instance for global use
export const healthCareService = new HealthCareService();
